/**
 * D420: the arena theme's map kit (`MapKit` in `iso-kit.ts`): a camp growing into a fortress.
 * Its plots are the farm's places under the arena producers' ids (the shared economy keeps every
 * position, D351), each drawn from `iso-kit-arena-art.ts`. The stronghold stands on its packed
 * earth and grass behind a wall that grows with the heart (`fortLevel`, D437: a timber palisade,
 * then a curtain wall with its walkway, braziers and gatehouse, then corner towers); the track is a dirt
 * road, cobbled and paved as it grows; round the land runs a moat, the stronghold's own water in
 * place of the farm's river, bridged where the road goes out. Beyond lies the realm: fields and
 * woods on a grid of lanes, cottages, and mountains along the back. Seasons and the hour stay
 * (D288): snow on the roofs in winter, windows and torches lit at night. Its 3D half is
 * `arenaThree` in `idle/three/arena.ts`.
 */
import { getIdleContent } from '@hedgerow/data';
import type { CountryItem, WorldLayout } from '../idle/map.ts';
import {
  ASSET_PALETTE,
  GOOD_WEATHER,
  LANDSCAPE,
  PHASE_LIGHT,
  PHASE_TIME,
  FIELD_CYCLE,
  READY_BADGE,
  SNOW,
} from '../idle/appearance.ts';
import { THEME_MAP_COLOURS } from '../idle/assets/idle-worlds-assets.js';
import type { IsoBuildingAt, Standing } from './IsoWorldMap.tsx';
import {
  TH,
  TW,
  alpha,
  assetLevel,
  depth,
  frontCorner,
  heartPlan,
  isPlan,
  levelHeight,
  mix,
  palette,
  parcelBoundaries,
  polyPath,
  project,
  rectPts,
  rnd,
  scatter,
  shade,
  tint,
  unionBounds,
  unionPath,
  type Bounds,
  type Heart,
  type IsoParcel,
  type MapTokens,
  type Palette,
  type Point,
  fameProgress,
  WINTER_COVER,
} from './iso.ts';
import { paintProp, type Prop, type Puff, type Ripple, pondRipples } from './iso-paint.ts';
import type { FlatEdges, FlatScenery, MapKit, PlotSlot } from './iso-kit.ts';
import { SLOTS as FARM_SLOTS, CHAIN_BUILDINGS as FARM_CHAINS } from './iso-kit-farm.ts';
import { hashXY, type Look, type Piece } from './iso-kit-compute-art.ts';
import { paintPiece } from './iso-kit-compute.ts';
import {
  A,
  BANNERS,
  GATES,
  PATHS,
  WINTER,
  buildingUnits,
  fortLevel,
  gateUnit,
  markUnits,
  unitTop,
  wallUnits,
  type ArenaPiece,
  type ArenaUnit,
} from './iso-kit-arena-art.ts';

/* ---------- the plots and chain buildings: the farm's places, by position (D351) ---------- */

/** The farm's slot for each position, under the arena producer at that position. */
function arenaPlots(): Record<string, PlotSlot> {
  const farm = getIdleContent('farm'),
    arena = getIdleContent('arena');
  const landOf = new Map(farm.map.lands.map((l, i) => [l.id, arena.map.lands[i]?.id ?? l.id]));
  const out: Record<string, PlotSlot> = {};
  farm.producers.forEach((p, i) => {
    const id = arena.producers[i]?.id;
    const s = FARM_SLOTS[p.id];
    if (!id || !s) return;
    out[id] = {
      x: s.x,
      y: s.y,
      w: s.w,
      h: s.h,
      kind: 'field',
      ...(s.chainReserve !== undefined ? { chainReserve: s.chainReserve } : {}),
      ...(s.land ? { land: landOf.get(s.land) ?? s.land } : {}),
    };
  });
  return out;
}

/** Each chain's building where the farm's chain at that position stands. */
function arenaChains(): Record<string, IsoBuildingAt> {
  const farm = getIdleContent('farm'),
    arena = getIdleContent('arena');
  const out: Record<string, IsoBuildingAt> = {};
  farm.chains.forEach((c, i) => {
    const id = arena.chains[i]?.id;
    const b = FARM_CHAINS[c.id];
    if (id && b) out[id] = { ...b, kind: 'shed', use: 'processing' };
  });
  return out;
}

/* ---------- the palette ---------- */

/** The green the stronghold's grass and the realm's meadows lean to. */
const GRASS = '#6FA352';

/** D420: the stronghold's own ground through the season's wash; the hour and weather as ever. */
function arenaPalette(
  season: string,
  time: string,
  tokens?: MapTokens | null,
  weather?: Parameters<typeof palette>[3],
): Palette {
  const P = palette(season, time, tokens, weather);
  const [g, gt] = P.S.ground;
  const frost = !!P.S.frost;
  return {
    ...P,
    soil: mix(A.earth, g, gt * 0.4),
    yard: mix(A.packed, g, gt * 0.3),
    road: mix('#8E7656', g, 0.1),
    track: mix('#8E7656', g, 0.1),
    waste: mix(A.gravel, g, gt * 0.3),
    hedge: A.timber,
    meadow: frost
      ? mix(mix(P.meadow, GRASS, 0.4), SNOW.colour, WINTER.grass)
      : mix(P.meadow, GRASS, 0.45),
    pasture: frost
      ? mix(mix(P.pasture, GRASS, 0.35), SNOW.colour, WINTER.grass * 0.8)
      : mix(P.pasture, GRASS, 0.4),
    water: mix(A.moat, P.water, 0.4),
  };
}

const NIGHT = new Set(['Night', 'Dusk']);
const lookOf = (P: Palette): Look => ({ night: NIGHT.has(P.T.label), winter: !!P.S.frost });

/* ---------- the realm round the stronghold ---------- */

const overlaps = (a: Bounds, b: Bounds): boolean =>
  a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

const pad = (b: Bounds, n: number): Bounds => ({
  x: b.x - n,
  y: b.y - n,
  w: b.w + 2 * n,
  h: b.h + 2 * n,
});

/** The moat's gap from the land in use, and its width. */
export const MOAT = { gap: 0.7, w: 0.9 } as const;

let landscapeCache: { key: string; out: CountryItem[] } | null = null;

/**
 * The realm round the stronghold, from its seed. Its items: `amoat` a stretch of the moat,
 * `aroad` the road out of the gate (and `abridge` where it crosses the moat), `alane` a lane
 * between lots, `alot` a lot (`k` field, meadow, wood or cottage; `r` a field's crop), `ascene`
 * and `asc` the asset module's country (`arenaScenery`), `amount` a mountain (`s` its height,
 * `w` its foot),
 * and the shared `chills`. Kept clear of the land in use and a strip round it, so the stronghold
 * growing only takes the nearest lots. A kind's scenery raises the hills and brings the woods.
 */
export function arenaLandscape(L: WorldLayout, plot: Bounds, lands: Bounds[]): CountryItem[] {
  const key = JSON.stringify([L.seed, plot, lands, L.gate, L.scenery]);
  if (landscapeCache?.key === key) return landscapeCache.out;
  const sc = L.scenery;
  const S = L.seed >>> 0;
  const R = LANDSCAPE.reach;
  const out: CountryItem[] = [];
  out.push({ t: 'chills', x: plot.x - R, y: plot.y - R, w: 0, h: 0, s: 0.8 * sc.hills });
  // The moat, all round the land in use.
  const m = pad(plot, MOAT.gap),
    o = pad(plot, MOAT.gap + MOAT.w);
  out.push(
    { t: 'amoat', x: o.x, y: o.y, w: o.w, h: MOAT.w },
    { t: 'amoat', x: o.x, y: m.y + m.h, w: o.w, h: MOAT.w },
    { t: 'amoat', x: o.x, y: m.y, w: MOAT.w, h: m.h },
    { t: 'amoat', x: m.x + m.w, y: m.y, w: MOAT.w, h: m.h },
  );
  const keep: Bounds[] = [pad(plot, 3), ...lands.map((b) => pad(b, 2))];
  // The road out of the gate, over the moat on its bridge, on to the edge of the realm.
  const g = L.gate;
  const dir: [number, number] = g.y >= L.height ? [0, 1] : g.x <= 0 ? [-1, 0] : [1, 0];
  const far = R + Math.max(plot.w, plot.h);
  const road: Bounds =
    dir[1] === 0
      ? { x: dir[0] < 0 ? g.x - far : g.x, y: g.y - 0.4, w: far, h: 0.8 }
      : { x: g.x - 0.4, y: g.y, w: 0.8, h: far };
  out.push({ t: 'aroad', ...road });
  for (const c of out.slice(1, 5)) {
    if (!overlaps(c, road)) continue;
    const x0 = Math.max(c.x, road.x) - (dir[1] ? 0.15 : 0),
      y0 = Math.max(c.y, road.y) - (dir[1] ? 0 : 0.15),
      x1 = Math.min(c.x + c.w, road.x + road.w) + (dir[1] ? 0.15 : 0),
      y1 = Math.min(c.y + c.h, road.y + road.h) + (dir[1] ? 0 : 0.15);
    out.push({ t: 'abridge', x: x0, y: y0, w: x1 - x0, h: y1 - y0, r: dir[1] ? 1 : 0 });
  }
  keep.push(pad(road, 1));
  // The mountains, along the back, beyond the back corner.
  const peaks = 7 + Math.round(sc.hills * 3);
  for (let i = 0; i < peaks; i++) {
    const a = (0.02 + 0.96 * hashXY(S, i, 11)) * (Math.PI / 2);
    const d = 20 + hashXY(S, i, 12) * 10;
    const x = plot.x + plot.w * 0.3 - Math.cos(a) * d,
      y = plot.y + plot.h * 0.3 - Math.sin(a) * d;
    const foot = 3 + hashXY(S, i, 13) * 3;
    out.push({
      t: 'amount',
      x,
      y,
      w: foot,
      h: foot,
      s: (3.5 + hashXY(S, i, 14) * 4) * (0.7 + sc.hills * 0.3),
    });
  }
  // The realm's lots on a grid of lanes: fields, meadows, woods and the odd cottage.
  const lot = 5,
    gap = 0.6;
  const cell = lot + gap;
  const x0 = Math.floor((plot.x - R) / cell) * cell,
    y0 = Math.floor((plot.y - R) / cell) * cell;
  const woods = 0.18 + Math.min(0.3, sc.copse * 0.08);
  for (let x = x0; x < plot.x + plot.w + R; x += cell)
    for (let y = y0; y < plot.y + plot.h + R; y += cell) {
      const b: Bounds = { x, y, w: lot, h: lot };
      if (keep.some((k) => overlaps(k, b))) continue;
      const roll = hashXY(x, y, S ^ 0x41);
      const k =
        roll < woods
          ? 'wood'
          : roll < woods + 0.12 * sc.village
            ? 'cottage'
            : roll < 0.75
              ? 'field'
              : 'meadow';
      out.push({
        t: 'alot',
        ...b,
        k,
        r: Math.floor(hashXY(x, y, S ^ 0x42) * 4),
        s: hashXY(x, y, S ^ 0x43) < 0.5 ? 1 : 0,
      });
      out.push({ t: 'alane', x: x - gap, y: y + lot, w: cell, h: gap });
      out.push({ t: 'alane', x: x + lot, y: y - gap, w: gap, h: cell });
      // D426: the trees and cottages standing are the module's (`arenaScenery`).
    }
  // D426: where the module's country walks from; what stands is walked as the world grows.
  out.push(
    { t: 'ascene', ...plot, r: 1 + ((L.seed >>> 0) % 9973) },
    ...lands.map((b) => ({ t: 'aland', ...b })),
  );
  landscapeCache = { key, out };
  return out;
}

/** D422, D426: how far the asset module's country reaches past the land, in tiles. */
export const SCENERY_REACH = LANDSCAPE.haze.far / 2;

/**
 * D426: where the asset module's country keeps clear: the land and a strip round it, the lands
 * beside it, the moat, its bridge and the road; its small things keep off the lanes and fields.
 * Both maps ask it of the same layout, so they keep the same things.
 */
export function sceneryClear(
  plot: Bounds,
  lands: readonly Bounds[],
  items: readonly CountryItem[],
): (x: number, z: number, big: boolean) => boolean {
  const pad: Record<string, number> = { amoat: 0.3, abridge: 0.3, aroad: 0.6 };
  const keep: Bounds[] = [pad1(plot, 1), ...lands.map((b) => pad1(b, 1))];
  for (const q of items) if (pad[q.t] !== undefined) keep.push(pad1(q, pad[q.t] as number));
  const small: Bounds[] = items
    .filter((q) => q.t === 'alane' || (q.t === 'alot' && q.k === 'field'))
    .map((q) => pad1(q, q.t === 'alane' ? 0.15 : -0.1));
  const inside = (list: Bounds[], x: number, z: number) =>
    list.some((b) => x >= b.x && x <= b.x + b.w && z >= b.y && z <= b.y + b.h);
  return (x, z, big) => !inside(keep, x, z) && (big || !inside(small, x, z));
}
const pad1 = (b: Bounds, n: number): Bounds => pad(b, n);

/**
 * D426: how far the world has grown, for the module's country: the producers unlocked over all
 * the theme's producers, 0 with the first plot and 1 with every producer.
 */
export function sceneryProgress(parcels: readonly IsoParcel[] | undefined): number {
  const total = getIdleContent('arena').producers.length;
  const n = new Set((parcels ?? []).filter((p) => p.t === 'field' && p.id).map((p) => p.id)).size;
  return total > 1 ? Math.min(1, Math.max(0, n - 1) / (total - 1)) : 1;
}

/**
 * D422, D426: the asset module's country (`scenery('arena', …)`) as the flat map paints it: the
 * same seeded walk over the same cells, so each tree, cottage, mountain and the late castle
 * stands where the 3D map's does, from the layout's `ascene` (the walk's bounds and seed) and
 * `aland` (each land it keeps clear of). Each `asc` is one thing standing (`k` pine, oak,
 * cottage, house, mountain or castle). Both maps pass the same `progress` (`sceneryProgress`).
 */
export function arenaScenery(items: readonly CountryItem[], progress: number): CountryItem[] {
  const walk = items.find((q) => q.t === 'ascene');
  if (!walk) return [];
  const seed = walk.r ?? 1;
  const plot: Bounds = { x: walk.x, y: walk.y, w: walk.w, h: walk.h };
  const lands = items.filter((q) => q.t === 'aland');
  const out: CountryItem[] = [];
  const clear = sceneryClear(plot, lands, items);
  const add = (q: CountryItem, big = false) => {
    if (clear(q.x, q.y, big)) out.push(q);
  };
  const r = (() => {
    let s = seed;
    return () => ((s = (s * 16807) % 2147483647) - 1) / 2147483646;
  })();
  const x0 = plot.x,
    z0 = plot.y,
    x1 = plot.x + plot.w,
    z1 = plot.y + plot.h,
    m = SCENERY_REACH;
  const cell = 3,
    near = cell * 0.6;
  for (let x = x0 - m; x < x1 + m; x += cell)
    for (let z = z0 - m; z < z1 + m; z += cell) {
      const cx = x + cell / 2,
        cz = z + cell / 2;
      if (cx > x0 - near && cx < x1 + near && cz > z0 - near && cz < z1 + near) continue;
      const k = r();
      if (cz < z0 - 3 && r() < 0.85) {
        const mz = cz - 3 - r() * 3,
          rad = 2.2 + r() * 1.8,
          h = 3.5 + r() * 4.5;
        r();
        add({ t: 'asc', k: 'mountain', x: cx, y: mz, w: rad, h: rad, s: h }, true);
      } else if (k < 0.4) r();
      else if (k < 0.7)
        for (let i = 0; i < 4; i++) {
          const tx = cx - 1 + r() * 2,
            tz = cz - 1 + r() * 2,
            s = 1 + r() * 0.6;
          const hedge = r() >= 0.5;
          const pine = hedge || (((tx * 7.3 + tz * 3.1) % 1) + 1) % 1 < 0.35;
          add({
            t: 'asc',
            k: pine ? 'pine' : 'oak',
            x: tx,
            y: tz,
            w: 0,
            h: 0,
            s: pine ? s * 1.15 : s,
          });
        }
      else if (k < 0.8 + progress * 0.15) {
        add({ t: 'asc', k: 'cottage', x: cx - 0.5, y: cz, w: 0.7, h: 0.55, s: 0.4 });
        if (progress > 0.4)
          add({ t: 'asc', k: 'house', x: cx + 0.5, y: cz + 0.4, w: 0.7, h: 0.55, s: 0.5 });
      }
    }
  if (progress > 0.6)
    add(
      {
        t: 'asc',
        k: 'castle',
        x: (x0 + x1) / 2 + 2,
        y: z0 - 7.5,
        w: 0,
        h: 0,
        s: 1 + (progress - 0.6) * 1.5,
      },
      true,
    );
  return out;
}

/** The realm's field colours by crop:wheat gold, barley pale, green shoots, ploughed earth. */
export const REALM_FIELDS = ['#C9AE5E', '#B9B47A', '#7FA052', '#8A6E4E'] as const;

/* ---------- painting the pieces in SVG ---------- */

const ZPX = (TW / 2 / Math.SQRT1_2) * Math.cos(Math.PI / 6);
const XPX = TW / 2 / Math.SQRT1_2;
const RX = TW / Math.SQRT2,
  RY = TH / Math.SQRT2;
const f1 = (n: number): string => n.toFixed(1);
const pts = (q: Point[]): string => polyPath(q);
const P3 = (x: number, y: number, z: number): Point => project(x, y, z * ZPX);
const snowed = (c: string, L: Look): string => (L.winter ? mix(c, SNOW.colour, WINTER.roof) : c);

/** The flat map's flicker for flames, frozen under reduced motion. */
const FLICKER_CSS =
  '<style>' +
  '@keyframes arn-flick{0%,100%{opacity:1}40%{opacity:.78}70%{opacity:.92}}' +
  '.arn-flick{animation:arn-flick 1.3s ease-in-out infinite}' +
  '@media (prefers-reduced-motion:reduce){.arn-flick{animation:none}}' +
  '</style>';

function paintCone(q: Extract<ArenaPiece, { s: 'cone' }>, L: Look, o: string[]): void {
  const [cx, cb] = P3(q.x, q.y, q.z),
    [, ct] = P3(q.x, q.y, q.z + q.h);
  const c = snowed(q.c, L);
  if (isPlan()) {
    o.push(`<circle cx="${f1(cx)}" cy="${f1(cb)}" r="${f1(q.r * 32)}" fill="${c}"/>`);
    return;
  }
  const rx = q.r * RX,
    ry = q.r * RY;
  o.push(
    `<path d="M${f1(cx - rx)} ${f1(cb)} L${f1(cx)} ${f1(ct)} L${f1(cx + rx)} ${f1(cb)} A${f1(rx)} ${f1(ry)} 0 0 1 ${f1(cx - rx)} ${f1(cb)} Z" fill="${shade(c, 0.06)}"/>`,
    `<path d="M${f1(cx + rx * 0.15)} ${f1(cb + ry * 0.99)} L${f1(cx)} ${f1(ct)} L${f1(cx + rx)} ${f1(cb)} A${f1(rx)} ${f1(ry)} 0 0 1 ${f1(cx + rx * 0.15)} ${f1(cb + ry * 0.99)} Z" fill="${shade(c, 0.22)}"/>`,
  );
}

function paintGable(q: Extract<ArenaPiece, { s: 'gable' }>, L: Look, o: string[]): void {
  const { x, y, w, d, z, h } = q;
  const c = snowed(q.c, L);
  if (isPlan()) {
    o.push(
      `<path d="${pts([P3(x, y, z), P3(x + w, y, z), P3(x + w, y + d, z), P3(x, y + d, z)])}" fill="${c}"/>`,
    );
    return;
  }
  const end = q.end ? shade(q.end, 0.18) : shade(c, 0.3);
  const t = z + h;
  type Face = { p: Array<[number, number, number]>; c: string };
  const faces: Face[] = q.along
    ? [
        {
          p: [
            [x, y, z],
            [x + w, y, z],
            [x + w, y + d / 2, t],
            [x, y + d / 2, t],
          ],
          c: tint(c, 0.06),
        },
        {
          p: [
            [x, y + d, z],
            [x + w, y + d, z],
            [x + w, y + d / 2, t],
            [x, y + d / 2, t],
          ],
          c: shade(c, 0.1),
        },
        {
          p: [
            [x, y, z],
            [x, y + d, z],
            [x, y + d / 2, t],
          ],
          c: end,
        },
        {
          p: [
            [x + w, y, z],
            [x + w, y + d, z],
            [x + w, y + d / 2, t],
          ],
          c: end,
        },
      ]
    : [
        {
          p: [
            [x, y, z],
            [x, y + d, z],
            [x + w / 2, y + d, t],
            [x + w / 2, y, t],
          ],
          c: tint(c, 0.06),
        },
        {
          p: [
            [x + w, y, z],
            [x + w, y + d, z],
            [x + w / 2, y + d, t],
            [x + w / 2, y, t],
          ],
          c: shade(c, 0.16),
        },
        {
          p: [
            [x, y, z],
            [x + w, y, z],
            [x + w / 2, y, t],
          ],
          c: end,
        },
        {
          p: [
            [x, y + d, z],
            [x + w, y + d, z],
            [x + w / 2, y + d, t],
          ],
          c: end,
        },
      ];
  const mid = (f: Face) => {
    let a = 0,
      b = 0;
    for (const [px, py] of f.p) {
      a += px;
      b += py;
    }
    return depth(a / f.p.length, b / f.p.length);
  };
  for (const f of faces.sort((a, b) => mid(a) - mid(b)))
    o.push(`<path d="${pts(f.p.map(([a, b, cz]) => P3(a, b, cz)))}" fill="${f.c}"/>`);
}

function paintFlame(q: Extract<ArenaPiece, { s: 'flame' }>, o: string[]): void {
  const [cx, cy] = P3(q.x, q.y, q.z);
  const r = Math.max(1.2, q.r * XPX),
    h = q.h * ZPX;
  const drop = (rr: number, hh: number, c: string) =>
    `<path d="M${f1(cx - rr)} ${f1(cy)} Q${f1(cx - rr)} ${f1(cy - hh * 0.55)} ${f1(cx)} ${f1(cy - hh)} Q${f1(cx + rr)} ${f1(cy - hh * 0.55)} ${f1(cx + rr)} ${f1(cy)} Q${f1(cx)} ${f1(cy + rr * 0.6)} ${f1(cx - rr)} ${f1(cy)} Z" fill="${c}"/>`;
  o.push(
    `<g class="arn-flick" style="animation-delay:-${(hashXY(q.x, q.y, 5) * 1.3).toFixed(2)}s">${drop(r, h, A.flame)}${drop(r * 0.55, h * 0.6, A.flameCore)}</g>`,
  );
}

function paintCrystal(q: Extract<ArenaPiece, { s: 'crystal' }>, L: Look, o: string[]): void {
  const [cx, cb] = P3(q.x, q.y, q.z),
    [, cm] = P3(q.x, q.y, q.z + q.h * 0.35),
    [, ct] = P3(q.x, q.y, q.z + q.h);
  const r = Math.max(1, q.r * XPX);
  if (L.night)
    o.push(`<circle cx="${f1(cx)}" cy="${f1(cm)}" r="${f1(r * 3.2)}" fill="${alpha(q.c, 0.22)}"/>`);
  o.push(
    `<path d="M${f1(cx)} ${f1(cb)} L${f1(cx - r)} ${f1(cm)} L${f1(cx)} ${f1(ct)} Z" fill="${q.c}"/>`,
    `<path d="M${f1(cx)} ${f1(cb)} L${f1(cx + r)} ${f1(cm)} L${f1(cx)} ${f1(ct)} Z" fill="${tint(q.c, 0.35)}"/>`,
  );
}

function paintFlag(q: Extract<ArenaPiece, { s: 'flag' }>, o: string[]): void {
  o.push(
    `<path d="${pts([P3(q.x, q.y, q.z), P3(q.x + q.w, q.y, q.z - q.h * 0.15), P3(q.x + q.w, q.y, q.z - q.h * 0.85), P3(q.x, q.y, q.z - q.h)])}" fill="${q.c}"/>`,
  );
}

function paintArenaPiece(q: ArenaPiece, P: Palette, L: Look, o: string[]): void {
  switch (q.s) {
    case 'cone':
      return paintCone(q, L, o);
    case 'gable':
      return paintGable(q, L, o);
    case 'flame':
      return paintFlame(q, o);
    case 'crystal':
      return paintCrystal(q, L, o);
    case 'flag':
      return paintFlag(q, o);
    default:
      return paintPiece(q as Piece, P, L, o);
  }
}

/** A unit's pieces back to front: by their foot's height, then by depth at this turn. */
function ordered(u: ArenaUnit): ArenaPiece[] {
  const key = (q: ArenaPiece): [number, number] => {
    if (q.s === 'rod')
      return [Math.min(q.a[2], q.b[2]), depth((q.a[0] + q.b[0]) / 2, (q.a[1] + q.b[1]) / 2)];
    if (q.s === 'box' || q.s === 'panel' || q.s === 'gable')
      return [q.z, depth(q.x + q.w / 2, q.y + q.d / 2)];
    if (q.s === 'flag') return [q.z - q.h, depth(q.x + q.w / 2, q.y)];
    return [q.z, depth(q.x, q.y)];
  };
  return [...u.pieces]
    .map((q) => ({ q, k: key(q) }))
    .sort((a, b) => a.k[0] - b.k[0] || a.k[1] - b.k[1])
    .map((e) => e.q);
}

function paintUnit(u: ArenaUnit, P: Palette, o: string[]): void {
  const L = lookOf(P);
  if (u.badge) {
    const { x, y, id, icon } = u.badge;
    paintProp({ k: 'ready', x, y, id, icon }, P, o);
  }
  if (!u.pieces.length) return;
  // A run of the wall is long and thin: no soft shade under it.
  if (!isPlan() && u.w > 0.05 && Math.max(u.w, u.d) < 3) {
    const [cx, cy] = project(u.x + u.w / 2, u.y + u.d / 2);
    o.push(
      `<ellipse cx="${f1(cx + 3)}" cy="${f1(cy + 2)}" rx="${f1((u.w + u.d) * TW * 0.24)}" ry="${f1((u.w + u.d) * TH * 0.24)}" fill="rgba(40,30,20,.14)"/>`,
    );
  }
  for (const q of ordered(u)) paintArenaPiece(q, P, L, o);
}

/* ---------- the realm, the ground, the wall and the weather ---------- */

/** Whether a footprint lies wholly behind the land in use at this turn. */
function behind(b: Bounds, plot: Bounds): boolean {
  const corners = (q: Bounds) =>
    [
      [q.x, q.y],
      [q.x + q.w, q.y],
      [q.x, q.y + q.h],
      [q.x + q.w, q.y + q.h],
    ].map(([a, c]) => depth(a as number, c as number));
  return Math.max(...corners(b)) < Math.min(...corners(plot)) - 1;
}

function paintCountry(s: FlatScenery, P: Palette, o: string[]): void {
  const { far: plot } = s;
  // D426: the module's country, walked at the world's own progress, as the 3D map walks it.
  // D430: grown with the world's years and generations, as the module grows it.
  const progress = fameProgress(sceneryProgress(s.parcels), s.age ?? { years: 0, generations: 0 });
  const country = [...s.country, ...arenaScenery(s.country, progress)];
  o.push(FLICKER_CSS);
  const R = LANDSCAPE.flatReach;
  const far = (c: string) => P.far(c);
  o.push(
    `<path d="${pts(rectPts(plot.x - R - 4, plot.y - R - 4, plot.w + 2 * R + 8, plot.h + 2 * R + 8))}" fill="${far(P.pasture)}"/>`,
  );
  const edge = { x0: plot.x - R, y0: plot.y - R, x1: plot.x + plot.w + R, y1: plot.y + plot.h + R };
  const clip = (c: Bounds): Bounds | null => {
    const x0 = Math.max(c.x, edge.x0),
      y0 = Math.max(c.y, edge.y0),
      x1 = Math.min(c.x + c.w, edge.x1),
      y1 = Math.min(c.y + c.h, edge.y1);
    return x1 > x0 && y1 > y0 ? { x: x0, y: y0, w: x1 - x0, h: y1 - y0 } : null;
  };
  const L = lookOf(P);
  const winter = L.winter;
  const quad = (b: Bounds, fill: string) =>
    o.push(`<path d="${pts(rectPts(b.x, b.y, b.w, b.h))}" fill="${fill}"/>`);
  for (const c of country) {
    if (c.t === 'alot') {
      const b = clip({ x: c.x + 0.15, y: c.y + 0.15, w: c.w - 0.3, h: c.h - 0.3 });
      if (!b) continue;
      const crop = REALM_FIELDS[(c.r ?? 0) % REALM_FIELDS.length] as string;
      const fill =
        c.k === 'field'
          ? far(winter ? mix(crop, SNOW.colour, WINTER.grass) : mix(crop, P.soil, 0.15))
          : c.k === 'wood'
            ? far(shade(P.pasture, 0.12))
            : far(P.meadow);
      quad(b, fill);
    } else if (c.t === 'alane' || c.t === 'aroad') {
      const b = clip(c);
      if (b) quad(b, far(P.road));
    } else if (c.t === 'amoat') {
      const b = clip(c);
      if (!b) continue;
      // The moat is near the land, so it keeps more of its colour than the far fields.
      const near = (k: string) => mix(k, far(k), 0.35);
      quad(b, near(shade(P.soil, 0.2)));
      quad(
        { x: b.x + 0.1, y: b.y + 0.1, w: Math.max(0, b.w - 0.2), h: Math.max(0, b.h - 0.2) },
        near(winter ? mix(P.water, '#DDE6EC', 0.6) : P.water),
      );
    }
  }
  for (const c of country)
    if (c.t === 'abridge') {
      const b = clip(c);
      if (b) quad(b, far(A.plank));
    }
  // What stands, where it lies behind the stronghold at this turn: trees, cottages, mountains.
  const stand: Array<{ d: number; u: ArenaUnit }> = [];
  for (const c of country) {
    if (c.t === 'cwood' || (c.t === 'asc' && (c.k === 'pine' || c.k === 'oak'))) {
      const x = c.t === 'cwood' ? c.x + 0.5 : c.x,
        y = c.t === 'cwood' ? c.y + 0.5 : c.y;
      if (!clip({ x: x - 0.01, y: y - 0.01, w: 0.02, h: 0.02 })) continue;
      const sz = c.s ?? 1;
      const pine = c.k === 'pine';
      const green = c.t === 'asc' ? (pine ? M.hedge : M.leaf) : pine ? A.pine : P.wood;
      const crown = far(winter ? mix(green, SNOW.colour, 0.35) : green);
      stand.push({
        d: depth(x, y),
        u: {
          x,
          y,
          w: 0,
          d: 0,
          pieces: pine
            ? [
                { s: 'cyl', x, y, r: 0.04, z: 0, h: 0.2 * sz, c: far(A.timberDark) },
                { s: 'cone', x, y, r: 0.3 * sz, z: 0.15 * sz, h: 0.75 * sz, c: crown },
              ]
            : [
                { s: 'cyl', x, y, r: 0.04, z: 0, h: 0.4 * sz, c: far(M.timberDark) },
                { s: 'ball', x, y, r: 0.36 * sz, z: 0.45 * sz, c: crown },
              ],
        },
      });
    } else if (c.t === 'asc' && (c.k === 'cottage' || c.k === 'house')) {
      const b = { x: c.x - c.w / 2, y: c.y - c.h / 2, w: c.w, h: c.h };
      if (!clip(b) || !behind(b, plot)) continue;
      const wall = c.k === 'cottage' ? M.white : M.timber,
        roof = c.k === 'cottage' ? M.thatch : M.roofRed;
      const u: ArenaUnit = {
        x: b.x,
        y: b.y,
        w: b.w,
        d: b.h,
        pieces: houseAt(c.x, c.y, c.w, c.h, c.s ?? 0.4, snowed(wall, L), snowed(roof, L)),
      };
      stand.push({ d: depth(c.x, c.y), u: toned(u, far) });
    } else if (c.t === 'asc' && c.k === 'castle') {
      const k = c.s ?? 1;
      const b = { x: c.x - 3.8 * k, y: c.y - 3 * k, w: 7.6 * k, h: 6 * k };
      // Wholly beyond the land's back edge, so the land painted after it hides it truly.
      const back = b.y + b.h < plot.y || b.x + b.w < plot.x;
      if (!clip({ x: c.x, y: c.y, w: 0.1, h: 0.1 }) || !back) continue;
      stand.push({ d: depth(c.x, c.y), u: toned(castleUnit(c.x, c.y, k), far) });
    } else if (c.t === 'amount' || (c.t === 'asc' && c.k === 'mountain')) {
      const b = { x: c.x - c.w, y: c.y - c.h, w: c.w * 2, h: c.h * 2 };
      if (!clip({ x: c.x, y: c.y, w: 0.1, h: 0.1 }) || !behind(b, plot)) continue;
      const rock = far(mix(A.rock, '#7D8A96', 0.4));
      stand.push({
        d: depth(c.x, c.y),
        u: {
          x: c.x,
          y: c.y,
          w: 0,
          d: 0,
          pieces: [
            { s: 'cone', x: c.x, y: c.y, r: c.w, z: -0.3, h: c.s ?? 4, c: rock, sides: 6 },
            {
              s: 'cone',
              x: c.x,
              y: c.y,
              r: c.w * 0.32,
              z: -0.3 + (c.s ?? 4) * 0.68,
              h: (c.s ?? 4) * 0.32,
              c: far(SNOW.colour),
              sides: 6,
            },
          ],
        },
      });
    }
  }
  stand.sort((a, b) => a.d - b.d);
  for (const s of stand) for (const q of ordered(s.u)) paintArenaPiece(q, P, L, o);
}

/** D426: the module's great castle on the hill, as the flat map paints it: curtain, towers, keep. */
function castleUnit(cx: number, cy: number, k: number): ArenaUnit {
  const pieces: ArenaPiece[] = [
    boxAt(cx, cy + 2.2 * k, 6 * k, 0.6, 0, 1.4 * k, M.stone, { top: M.stoneCap }),
    boxAt(cx, cy - 2.2 * k, 6 * k, 0.6, 0, 1.4 * k, M.stone, { top: M.stoneCap }),
    boxAt(cx - 3 * k, cy, 0.6, 4.4 * k, 0, 1.4 * k, M.stone, { top: M.stoneCap }),
    boxAt(cx + 3 * k, cy, 0.6, 4.4 * k, 0, 1.4 * k, M.stone, { top: M.stoneCap }),
    boxAt(cx, cy, 2.4, 2.0, 0, 3.4 * k, M.stone, { top: M.stoneCap }),
  ];
  for (const [a, b] of [
    [-1, -1],
    [1, -1],
    [-1, 1],
    [1, 1],
  ] as const)
    pieces.push(
      { s: 'cyl', x: cx + a * 3 * k, y: cy + b * 2.2 * k, r: 0.75, z: 0, h: 2.4 * k, c: M.stone },
      {
        s: 'cone',
        x: cx + a * 3 * k,
        y: cy + b * 2.2 * k,
        r: 0.9,
        z: 2.4 * k,
        h: 1.3,
        c: M.slate,
        sides: 6,
      },
    );
  return { x: cx - 3.8 * k, y: cy - 3 * k, w: 7.6 * k, d: 6 * k, pieces };
}

/** A unit with every colour hazed into the far country. */
function toned(u: ArenaUnit, tone: (c: string) => string): ArenaUnit {
  return {
    ...u,
    pieces: u.pieces.map((q) => {
      if (q.s === 'flame') return q;
      const out = { ...q, c: tone(q.c) } as ArenaPiece;
      if (out.s === 'gable' && out.end) out.end = tone(out.end);
      if (out.s === 'box') {
        if (out.top) out.top = tone(out.top);
        if (out.marks) out.marks = { ...out.marks, c: tone(out.marks.c) };
      }
      return out;
    }),
  };
}

/** The ground a producer's plot stands on, by what it is: true to each place's use. */
const PAD: Record<string, string> = {
  campfires: A.packed,
  lumber_camps: '#7E6A4C',
  quarries: A.gravel,
  forges: '#7A6A58',
  guard_houses: A.flags,
  stables: '#A68F62',
  shrines: A.flags,
  training_grounds: A.sand,
  great_halls: A.packed,
  trading_posts: '#9E978A',
  crystal_mines: '#6F6B64',
  libraries: A.flags,
  dragon_roosts: '#5E554C',
  star_spires: '#C9C3B6',
};
/** Producers that stand on the grass, not a laid pad. */
const ON_GRASS = new Set([
  'tents',
  'watchtowers',
  'archery_ranges',
  'wizard_towers',
  'spirit_springs',
]);

/** Whether a parcel is grass rather than worked or laid ground. */
export const onGrass = (p: IsoParcel): boolean =>
  p.t === 'meadow' || p.t === 'pasture' || (p.t === 'field' && ON_GRASS.has(p.id ?? ''));

/** A parcel's ground: a plot's earth or grass, the yard laid with its path, the grass. */
export function groundColour(p: IsoParcel, P: Palette): string {
  const winter = !!P.S.frost;
  const snow = (c: string, k = 1) => (winter ? mix(c, SNOW.colour, WINTER.hard * k) : c);
  switch (p.t) {
    case 'field':
      if (ON_GRASS.has(p.id ?? '')) return mix(P.meadow, P.soil, 0.12);
      return snow(mix(PAD[p.id ?? ''] ?? A.earth, P.soil, 0.25), 0.8);
    case 'yard':
      return snow(mix(PATHS[p.path ?? ''] ?? A.packed, P.yard, 0.2), 0.8);
    case 'pond':
      return shade(P.water, 0.1);
    case 'waste':
      return snow(P.waste, 0.6);
    case 'meadow':
      return p.mark || p.ground ? snow(mix(A.flags, P.soil, 0.3), 0.8) : P.meadow;
    default:
      return snow(P.pasture);
  }
}

function paintGround(p: IsoParcel, P: Palette, o: string[]): void {
  if (!p.tiles.length) return;
  const fill = groundColour(p, P);
  o.push(
    `<path data-testid="iso-parcel" d="${unionPath(p.tiles)}" fill="${fill}" stroke="${shade(fill, 0.12)}" stroke-width="1"/>`,
  );
  if (p.t === 'pond')
    o.push(`<path d="${unionPath(p.tiles)}" fill="none" stroke="${A.stone}" stroke-width="2.4"/>`);
}

/** The wall decorations' state, read once per paint: the banners' colour and the gate's. */
function paintEdges(e: FlatEdges, P: Palette, o: string[]): void {
  const { parcels, outline, lands, hedges, decor } = e;
  // Low earth banks between the plots.
  for (const g of parcelBoundaries(parcels.map((p) => p.tiles))) {
    const a = project(g.x1, g.y1),
      b = project(g.x2, g.y2);
    o.push(
      `<line x1="${f1(a[0])}" y1="${f1(a[1])}" x2="${f1(b[0])}" y2="${f1(b[1])}" stroke="${alpha('#6B5A44', 0.45)}" stroke-width="1.4"/>`,
    );
  }
  if (!hedges) return;
  const level = fortLevel(parcels);
  const flagC = BANNERS[decor?.hedge ?? ''] ?? A.red;
  const units = wallUnits(
    outline?.hedges ?? [],
    lands,
    level,
    flagC,
    decor?.fence,
    lookOf(P).night,
  );
  const g = decor?.gateAt;
  if (g && !isPlan())
    units.push(gateUnit(g, level, GATES[decor?.gate ?? ''] ?? A.log, flagC, decor?.fence));
  units.sort((a, b) => depth(a.x + a.w / 2, a.y + a.d / 2) - depth(b.x + b.w / 2, b.y + b.d / 2));
  for (const u of units) paintUnit(u, P, o);
}

/** Snow falling over the stronghold in winter. */
function paintWeather(b: Bounds, P: Palette, o: string[]): void {
  if (!P.S.frost) return;
  const rf = rnd(5);
  for (let i = 0; i < 120; i++) {
    const [cx, cy] = project(b.x - 1 + rf() * (b.w + 2), b.y - 1 + rf() * (b.h + 2));
    o.push(
      `<circle cx="${cx.toFixed(0)}" cy="${(cy - rf() * 90).toFixed(0)}" r="${(0.8 + rf() * 1.2).toFixed(1)}" fill="#ffffff" opacity=".55"/>`,
    );
  }
}

/* ---------- D422: the asset module's plots, heart and fill, as both renderers place them ---------- */

// D440: the theme's accent and money colours over the game's palette, as the 3D map takes them.
const M = { ...ASSET_PALETTE, ...THEME_MAP_COLOURS.arena };

/**
 * D422, D424: where a piece from the asset module stands, carried on a placeholder unit so the
 * flat map paints simple shapes there and the 3D map builds the module's own piece in its place:
 * a producer's hero (`i` its index, `level` and `units`) on its plot, or the seeded courtyard
 * fill on bare land.
 */
export type ArenaAssetPlace =
  | {
      kind: 'hero';
      i: number;
      level: number;
      units: number;
      x: number;
      y: number;
      w: number;
      h: number;
    }
  | { kind: 'fill'; seed: number; x: number; y: number; w: number; h: number };

/** A unit that marks a piece of the asset module. */
export type ArenaAssetUnit = ArenaUnit & { asset?: ArenaAssetPlace };

/** The asset module's piece a placeholder unit stands for, if any. */
export const assetOf = (u: ArenaUnit): ArenaAssetPlace | undefined => (u as ArenaAssetUnit).asset;

/** A box piece by its middle, for the shapes below. */
function boxAt(
  cx: number,
  cy: number,
  w: number,
  d: number,
  z: number,
  h: number,
  c: string,
  extra: { top?: string; glow?: boolean; roof?: boolean } = {},
): ArenaPiece {
  return { s: 'box', x: cx - w / 2, y: cy - d / 2, w, d, z, h, c, roof: true, ...extra };
}

/** A walled house by its middle with a gabled roof along its longer side, as the module's `roofed`. */
function houseAt(
  cx: number,
  cy: number,
  w: number,
  d: number,
  h: number,
  wall: string,
  roof: string,
  z = 0,
  roofH = h * 0.8,
): ArenaPiece[] {
  return [
    boxAt(cx, cy, w, d, z, h, wall, { roof: false }),
    {
      s: 'gable',
      x: cx - w / 2 - 0.02,
      y: cy - d / 2 - 0.02,
      w: w + 0.04,
      d: d + 0.04,
      z: z + h,
      h: roofH,
      c: roof,
      along: w >= d,
      end: wall,
    },
  ];
}

/** How the module scales a hero at a level: its stage's size, then its milestones' height. */
function heroScale(level: number): { s: number; ly: number } {
  return {
    s: ([0.66, 0.82, 1][Math.min(2, level)] ?? 1) * 1.08,
    ly: levelHeight(level),
  };
}

/** The producer's hero as the flat map paints it: its hall and its milestone pieces. */
function heroUnit(p: IsoParcel, b: Bounds): ArenaAssetUnit | null {
  const content = getIdleContent('arena');
  const i = content.producers.findIndex((q) => q.id === p.id);
  const prod = content.producers[i];
  if (!prod) return null;
  const units = p.owned ?? 0;
  const level = assetLevel(
    units,
    prod.milestones.map((m) => m.owned),
  );
  const cx = b.x + b.w / 2,
    cy = b.y + b.h / 2;
  const { s, ly } = heroScale(level);
  const pieces: ArenaPiece[] = [
    ...houseAt(cx, cy - 0.2 * s, 1.6 * s, 1.1 * s, 0.7 * ly * s, M.stone, M.slate),
    boxAt(cx, cy + 0.36 * s, 0.24 * s, 0.03, 0, 0.26 * ly * s, M.timberDark, { roof: false }),
  ];
  if (units >= 10) pieces.push(...houseAt(cx - 1.05, cy + 0.9, 0.5, 0.4, 0.3, M.timber, M.thatch));
  if (units >= 25) pieces.push(...houseAt(cx + 0.95, cy + 0.9, 0.8, 0.6, 0.45, M.stone, M.roofRed));
  if (units >= 50) pieces.push(...houseAt(cx - 1.05, cy - 0.3, 0.7, 1.0, 0.6, M.stone, M.slate));
  if (units >= 100)
    pieces.push(
      { s: 'cyl', x: cx + 1.15, y: cy - 0.6, r: 0.25, z: 0, h: 1.9, c: M.stone },
      { s: 'cone', x: cx + 1.15, y: cy - 0.6, r: 0.3, z: 1.9, h: 0.6, c: M.slate, sides: 6 },
    );
  return {
    x: b.x,
    y: b.y,
    w: b.w,
    d: b.h,
    pieces,
    id: p.id,
    asset: { kind: 'hero', i, level, units, x: b.x, y: b.y, w: b.w, h: b.h },
  };
}

/**
 * Where a hero's smoke rises, from the module's own pieces: the campfires' ring and the forges'
 * chimney, scaled as the module scales the hero, which it centres on its plot.
 */
function heroSmoke(p: IsoParcel): Array<[number, number, number]> {
  if (p.id !== 'campfires' && p.id !== 'forges') return [];
  const b = unionBounds(p.tiles);
  if (!b.w) return [];
  const prod = getIdleContent('arena').producers.find((q) => q.id === p.id);
  const level = assetLevel(p.owned ?? 0, prod?.milestones.map((m) => m.owned) ?? []);
  const { s, ly } = heroScale(level);
  const cx = b.x + b.w / 2,
    cy = b.y + b.h / 2;
  return p.id === 'campfires'
    ? [[cx, cy, 0.55 * s * ly]]
    : [[cx + 0.45 * s, cy - 0.55 * s, 1.86 * s * ly]];
}

/** The module's seeded random numbers (Park and Miller), so both maps draw the same fill. */
function parkMiller(seed: number): () => number {
  let s = seed;
  return () => ((s = (s * 16807) % 2147483647) - 1) / 2147483646;
}

/**
 * D422: the courtyard fill on a free `w` by `d` rectangle, as the module's `arenaCourtyard`
 * places it from the same seed: rows of crops, banners on poles, carts and trees.
 */
function fillUnit(x: number, y: number, w: number, d: number, seed: number): ArenaAssetUnit {
  const r = parkMiller(seed);
  const pieces: ArenaPiece[] = [];
  const n = Math.max(2, Math.round((w * d) / 3));
  for (let i = 0; i < n; i++) {
    const lx = 0.4 + r() * (w - 0.8),
      lz = 0.4 + r() * (d - 0.8),
      k = r();
    const px = x + lx,
      py = y + lz;
    if (k < 0.35) {
      for (let j = 0; j < 4; j++)
        pieces.push(
          boxAt(px - 0.3 + j * 0.2, py, 0.16, 0.8, 0, 0.1, j % 2 ? M.carrotTop : M.wheat),
        );
    } else if (k < 0.55) {
      const c = r() < 0.5 ? M.banner : M.bannerBlue;
      pieces.push(boxAt(px, py, 0.04, 0.04, 0, 0.8, M.timberDark, { roof: false }), {
        s: 'flag',
        x: px + 0.02,
        y: py,
        z: 0.75,
        w: 0.22,
        h: 0.3,
        c,
      });
    } else if (k < 0.75) {
      r();
      r();
      pieces.push(boxAt(px, py, 0.5, 0.3, 0.1, 0.18, M.timber));
    } else if ((((lx * 7.3 + lz * 3.1) % 1) + 1) % 1 < 0.35) {
      // The module's `tree`: a pine where its spot says so, a broadleaf else.
      pieces.push(
        { s: 'cyl', x: px, y: py, r: 0.04, z: 0, h: 0.18, c: M.timberDark },
        { s: 'cone', x: px, y: py, r: 0.28, z: 0.15, h: 0.8, c: M.hedge },
      );
    } else
      pieces.push(
        { s: 'cyl', x: px, y: py, r: 0.04, z: 0, h: 0.3, c: '#6B5A44' },
        { s: 'ball', x: px, y: py, r: 0.3, z: 0.25, c: M.leaf },
      );
  }
  return { x, y, w, d, pieces, asset: { kind: 'fill', seed, x, y, w, h: d } };
}

/** A seed for a rectangle of fill from where it lies: always the same, never 0. */
function fillSeed(x: number, y: number): number {
  return 1 + ((((x + 64) * 7919 + (y + 64) * 104729) >>> 0) % 2147483640);
}

/**
 * D422: bare land broken into rectangles, back row first, each as wide then as deep as the free
 * tiles allow; those two by two or larger take the courtyard fill, the tiles left keep their trees.
 */
function fillRects(tiles: readonly string[]): { rects: Bounds[]; left: string[] } {
  const free = new Set(tiles);
  const order = [...tiles]
    .map((k) => k.split(',').map(Number) as [number, number])
    .sort((a, b) => a[1] - b[1] || a[0] - b[0]);
  const rects: Bounds[] = [];
  for (const [x, y] of order) {
    if (!free.has(`${x},${y}`)) continue;
    let w = 1;
    while (free.has(`${x + w},${y}`)) w++;
    let h = 1;
    while (Array.from({ length: w }, (_, i) => free.has(`${x + i},${y + h}`)).every(Boolean)) h++;
    if (w < 2 || h < 2) continue;
    for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) free.delete(`${x + i},${y + j}`);
    rects.push({ x, y, w, h });
  }
  return { rects, left: tiles.filter((k) => free.has(k)) };
}

/** D422: the heart's stage from its drivers, as the module reads them. */
function heartStage(h: Heart): number {
  const byP = h.producers >= 15 ? 2 : h.producers >= 7 ? 1 : 0;
  const byY = h.years >= 7 ? 2 : h.years >= 3 ? 1 : 0;
  return Math.max(byP, byY);
}

/**
 * D422, D423: the heart as the flat map paints it, from the same plan as the 3D map's: the court
 * and its sand path, the hall and its fire, the curtain wall and gatehouse from the second stage,
 * the keep and the corner towers at the third. Laid out on the module's 7 by 7 plot and scaled
 * evenly onto the yard, mirrored with the world.
 */
function heartUnit(h: Heart): ArenaUnit {
  const plan = heartPlan(h);
  const k = plan.size / 7,
    m = h.dir;
  const st = heartStage(h);
  const X = (lx: number) => plan.x + (3.5 + m * lx) * k,
    Y = (ly: number) => plan.y + (3.5 + ly) * k;
  const box = (
    lx: number,
    ly: number,
    w: number,
    d: number,
    z: number,
    hh: number,
    c: string,
    extra: { top?: string; glow?: boolean; roof?: boolean } = {},
  ): ArenaPiece => boxAt(X(lx), Y(ly), w * k, d * k, z * k, hh * k, c, extra);
  const hallW = [2.4, 3.0, 3.0][st] ?? 2.4,
    hallD = [1.4, 1.6, 1.6][st] ?? 1.4,
    hallH = [0.9, 1.1, 1.1][st] ?? 0.9;
  const pieces: ArenaPiece[] = [
    box(0, 0, 7, 7, 0, 0.03, M.courtGrass, { roof: false }),
    box(0, 0, 0.9, 7, 0.03, 0.01, M.sand, { roof: false }),
    ...houseAt(X(0), Y(-1.3), hallW * k, hallD * k, hallH * k, M.stone, M.roofRed, 0, 1.4 * k),
    { s: 'cyl', x: X(0), y: Y(1.6), r: 0.4 * k, z: 0, h: 0.1 * k, c: M.stoneDark },
    { s: 'flame', x: X(0), y: Y(1.6), z: 0.1 * k, r: 0.2 * k, h: 0.45 * k },
  ];
  if (st >= 1) {
    const W2 = 3.15;
    pieces.push(
      box(0, -W2, 6.3, 0.3, 0, 0.9, M.stone, { top: M.stoneCap }),
      box(-W2, 0, 0.3, 6.3, 0, 0.9, M.stone, { top: M.stoneCap }),
      box(W2, 0, 0.3, 6.3, 0, 0.9, M.stone, { top: M.stoneCap }),
      box(-2.0, W2, 2.3, 0.3, 0, 0.9, M.stone, { top: M.stoneCap }),
      box(2.0, W2, 2.3, 0.3, 0, 0.9, M.stone, { top: M.stoneCap }),
      box(0, W2, 1.2, 0.8, 0, 1.5, M.stoneDark, { top: M.stoneCap }),
    );
  }
  if (st >= 2) {
    pieces.push(box(0.1, -1.3, 2.2, 2.2, 0, 3.8, M.stone, { top: M.stoneCap }));
    for (const [a, b] of [
      [-3, -3],
      [3, -3],
      [-3, 3],
      [3, 3],
    ] as const)
      pieces.push(
        { s: 'cyl', x: X(a), y: Y(b), r: 0.5 * k, z: 0, h: 2.6 * k, c: M.stone },
        { s: 'cone', x: X(a), y: Y(b), r: 0.62 * k, z: 2.6 * k, h: 1.2 * k, c: M.slate, sides: 6 },
      );
  }
  return { x: plan.x, y: plan.y, w: plan.size, d: plan.size, pieces };
}

/**
 * D423: the store's bays beside the heart, one for every storage upgrade, drawn the same in both
 * maps: a stone storehouse each under a slate roof, its door to the front.
 */
export function storeUnits(h: Heart): ArenaUnit[] {
  return heartPlan(h).bays.map((b) => ({
    x: b.x,
    y: b.y,
    w: b.w,
    d: b.h,
    pieces: [
      ...houseAt(b.x + b.w / 2, b.y + b.h / 2, b.w, b.h, 0.5, M.stone, M.slate, 0, 0.32),
      boxAt(b.x + b.w / 2, b.y + b.h - 0.01, b.w * 0.3, 0.03, 0, 0.3, M.timberDark, {
        roof: false,
      }),
    ],
  }));
}

/**
 * D422, D423: a building's units, the heart's in place of the keep's: the 3D map builds the
 * module's heart and takes only the store's bays from here (`flat` off); the flat map paints both.
 */
export function arenaBuildingUnits(b: IsoBuildingAt, flat = true): ArenaUnit[] {
  if (!b.heart) return buildingUnits(b);
  return flat ? [heartUnit(b.heart), ...storeUnits(b.heart)] : storeUnits(b.heart);
}

/** D422: each plot's own edge on the flat map: its court of grass, its stone kerb and sand path. */
function paintPlotEdges(parcels: IsoParcel[], P: Palette, o: string[]): void {
  // D440: the court's grass under winter's snow, as far as it has come.
  const court = mix(M.courtGrass, SNOW.colour, WINTER_COVER.grass * (P.snow ?? 0));
  if (isPlan()) return;
  for (const p of parcels) {
    if (p.t !== 'field' || !p.id || !p.tiles.length) continue;
    const b = unionBounds(p.tiles);
    o.push(`<path d="${pts(rectPts(b.x, b.y, b.w, b.h))}" fill="${court}"/>`);
    o.push(
      `<path d="${pts(rectPts(b.x + b.w / 2 - 0.25, b.y + b.h * 0.555, 0.5, b.h * 0.44))}" fill="${M.sand}"/>`,
    );
    const t = 0.2;
    const run = (x0: number, y0: number, len: number, along: boolean) => {
      const n = Math.max(1, Math.round(len));
      for (let i = 0; i < n; i++) {
        const a = (len * i) / n,
          e = (len * (i + 1)) / n;
        const q = along ? rectPts(x0 + a, y0, e - a, t) : rectPts(x0, y0 + a, t, e - a);
        o.push(`<path d="${pts(q)}" fill="${i % 2 ? M.stoneDark : M.stone}"/>`);
      }
    };
    run(b.x, b.y, b.w, true);
    run(b.x, b.y + b.h - t, b.w, true);
    run(b.x, b.y, b.h, false);
    run(b.x + b.w - t, b.y, b.h, false);
  }
}

/* ---------- what stands on the land ---------- */

const piece = (u: ArenaUnit): Prop => ({ k: 'piece', x: u.x, y: u.y, w: u.w, h: u.d, unit: u });

/** A plot's front corner, a little in, at this turn: where its sign and its stewards stand. */
function corner(b: Bounds, k: number, inset: number): Point {
  const cs: Point[] = [
    [b.x, b.y],
    [b.x + b.w, b.y],
    [b.x + b.w, b.y + b.h],
    [b.x, b.y + b.h],
  ];
  const c = cs[(frontCorner(b.x, b.y, b.w, b.h) + k) % 4] as Point;
  const cx = b.x + b.w / 2,
    cy = b.y + b.h / 2;
  return [c[0] + Math.sign(cx - c[0]) * inset, c[1] + Math.sign(cy - c[1]) * inset];
}

/** Everything standing on a parcel, placed for both renderers. */
export function arenaProps(
  p: IsoParcel,
  showSigns: boolean,
  _L: Look = { night: false, winter: false },
): Prop[] {
  const out: Prop[] = [];
  const b = unionBounds(p.tiles);
  if (!b.w) return out;
  if (p.t === 'field' && p.id) {
    // D422: the producer's hero from the asset module (a placeholder the flat map paints simply).
    const hero = heroUnit(p, b);
    const units = hero ? [hero] : [];
    for (const u of units) out.push(piece(u));
    if (p.sign && showSigns) {
      const at = corner(b, 0, 0.12);
      out.push({ k: 'sign', x: at[0], y: at[1], text: p.sign });
    }
    // D268: a steward by the plot once one is hired, more with the milestones.
    const staff = (p.hand ? 1 : 0) + Math.min(2, Math.floor((p.tier ?? 0) / 5));
    for (let i = 0; i < staff; i++) {
      const at = corner(b, 3 - (i % 2) * 2, 0.08 + i * 0.12);
      out.push({ k: 'worker', x: at[0], y: at[1], ...(p.workers ? { look: p.workers } : {}) });
    }
    if (p.burst)
      out.push({ k: 'burst', x: b.x + b.w / 2, y: b.y + b.h / 2, r: Math.max(b.w, b.h) / 2 });
    // D345: the ready badge, with the arena producer's own icon.
    if (p.ready) {
      const [fx, fy] = corner(b, 0, 0.02);
      out.push(
        piece({
          x: fx - 0.01,
          y: fy - 0.01,
          w: 0.02,
          d: 0.02,
          pieces: [],
          badge: {
            x: b.x + b.w / 2,
            y: b.y + b.h / 2,
            id: p.id,
            icon: `producers_${p.id}`,
            lift: units.reduce((t, u) => Math.max(t, unitTop(u)), 0.5) + 0.45,
          },
        }),
      );
    }
    return out;
  }
  if (p.t === 'meadow' && p.mark) {
    for (const u of markUnits(p.mark, b.x + b.w / 2, b.y + b.h / 2)) out.push(piece(u));
    return out;
  }
  if (p.t === 'meadow' && p.ground) {
    // D327: a monument ground: each later tournament's mark, small, in rows.
    const marks = p.ground.marks.slice(0, 8);
    marks.forEach((m, i) => {
      const x = b.x + 0.5 + (i % 4) * ((b.w - 1) / 3),
        y = b.y + 0.6 + Math.floor(i / 4) * (b.h - 1.2);
      out.push(
        piece({
          x: x - 0.2,
          y: y - 0.2,
          w: 0.4,
          d: 0.4,
          pieces: [
            {
              s: 'box',
              x: x - 0.12,
              y: y - 0.1,
              w: 0.24,
              d: 0.2,
              z: 0,
              h: 0.24,
              c: A.stone,
              roof: true,
            },
            m === 'beacon' || m === 'fire_bowl'
              ? { s: 'flame', x, y, z: 0.24, r: 0.05, h: 0.12 }
              : { s: 'cone', x, y, r: 0.08, z: 0.24, h: 0.12, c: A.gold, sides: 4 },
          ],
        }),
      );
    });
    return out;
  }
  if (p.t === 'meadow') {
    // D422: the courtyard fill on bare land two by two or larger, from a seed both maps share;
    // a few oaks and pines on the grass left.
    const { rects, left } = fillRects(p.tiles);
    for (const q of rects) out.push(piece(fillUnit(q.x, q.y, q.w, q.h, fillSeed(q.x, q.y))));
    const r = rnd(b.x * 31 + b.y * 17 + 3);
    for (const [x, y] of scatter(left, r, Math.round(left.length * 0.12), 0.3)) {
      const s = 0.8 + r() * 0.4;
      const pine = r() < 0.4;
      out.push(
        piece({
          x: x - 0.15,
          y: y - 0.15,
          w: 0.3,
          d: 0.3,
          pieces: pine
            ? [
                { s: 'cyl', x, y, r: 0.03, z: 0, h: 0.12 * s, c: A.timberDark },
                { s: 'cone', x, y, r: 0.2 * s, z: 0.1 * s, h: 0.5 * s, c: A.pine },
              ]
            : [
                { s: 'cyl', x, y, r: 0.035, z: 0, h: 0.22 * s, c: '#6B5A44' },
                { s: 'ball', x, y, r: 0.22 * s, z: 0.14 * s, c: A.leaf },
              ],
        }),
      );
    }
    return out;
  }
  if (p.t === 'yard') {
    // Barrels and crates by the keep, a cart, a pair of torches.
    const y = b.y + b.h - 0.7;
    for (let x = b.x + 0.6, i = 0; x < b.x + b.w - 0.4; x += 1.2, i++) {
      if (hashXY(x, y, 21) < 0.4) continue;
      out.push(
        piece({
          x: x - 0.2,
          y: y - 0.2,
          w: 0.4,
          d: 0.4,
          pieces:
            i % 2
              ? [
                  { s: 'cyl', x, y, r: 0.1, z: 0, h: 0.2, c: A.log, top: A.logEnd },
                  {
                    s: 'cyl',
                    x: x + 0.2,
                    y: y + 0.05,
                    r: 0.1,
                    z: 0,
                    h: 0.2,
                    c: A.log,
                    top: A.logEnd,
                  },
                ]
              : [
                  {
                    s: 'box',
                    x: x - 0.12,
                    y: y - 0.1,
                    w: 0.24,
                    d: 0.2,
                    z: 0,
                    h: 0.18,
                    c: A.plank,
                    roof: true,
                  },
                ],
        }),
      );
    }
  }
  return out;
}

/** Paint one standing thing: a unit of pieces, or one of the shared props (staff, sign, badge). */
function paintArenaProp(s: Prop, P: Palette, o: string[]): void {
  if (s.k === 'piece') return paintUnit(s.unit as ArenaUnit, P, o);
  if (s.k === 'build') {
    for (const u of arenaBuildingUnits(s)) paintUnit(u, P, o);
    return;
  }
  paintProp(s, P, o);
}

/** Every unit standing in the stronghold: the buildings', the plots' and the marks'. */
function allUnits(buildings: IsoBuildingAt[], parcels: IsoParcel[]): ArenaUnit[] {
  const L: Look = { night: false, winter: false };
  const out = buildings.flatMap((b) => arenaBuildingUnits(b));
  for (const p of parcels)
    for (const s of arenaProps(p, false, L)) if (s.k === 'piece') out.push(s.unit as ArenaUnit);
  return out;
}

/** D350: how tall the stronghold stands, in world units, for the 3D camera. */
export function strongholdTop(buildings: IsoBuildingAt[], parcels: IsoParcel[]): number {
  let t = 1.4;
  for (const u of allUnits(buildings, parcels)) t = Math.max(t, unitTop(u));
  return t + 0.15;
}

/** The smoke's sources: the campfires and the forges' chimneys, the keep's hearth else. */
export function smokeTops(
  buildings: IsoBuildingAt[],
  parcels: readonly IsoParcel[],
  max: number,
  chimney: [number, number, number] | null = null,
): Array<[number, number, number]> {
  const out: Array<[number, number, number]> = [];
  for (const p of parcels) for (const t of heroSmoke(p)) if (out.length < max) out.push(t);
  if (!out.length) {
    const keep = buildings.find((b) => b.kind === 'house');
    const h = keep?.heart;
    if (chimney) out.push(chimney);
    else if (h) {
      // D427: the heart's hall chimney (the 3D map passes the module's own `userData.chimney`);
      // the flat map has no module, so it reads the same place off the heart's plan.
      const plan = heartPlan(h),
        k = plan.size / 7,
        st = heartStage(h);
      const w = [2.4, 3.0, 3.0][st] ?? 2.4,
        d = [1.4, 1.6, 1.6][st] ?? 1.4,
        hh = [0.9, 1.1, 1.1][st] ?? 0.9;
      out.push([
        plan.x + (3.5 + h.dir * w * 0.32) * k,
        plan.y + (3.5 - 1.3 - d * 0.15) * k,
        (hh * 1.4 + 0.35) * k,
      ]);
    } else if (keep) out.push([keep.x + 0.4, keep.y + 0.4, 1.4]);
  }
  return out;
}

/* ---------- the kit ---------- */

/** D420: the arena theme's map kit. */
export const arenaKit: MapKit = {
  id: 'arena',
  plots: arenaPlots(),
  chainBuildings: arenaChains(),
  landscape: arenaLandscape,
  edgeWater: false,
  fieldCycle: FIELD_CYCLE,
  palette: arenaPalette,
  phaseTime: (phase) => PHASE_TIME[phase] ?? 'morning',
  light: (phase) => PHASE_LIGHT[phase] ?? PHASE_LIGHT.day!,
  goodWeather: GOOD_WEATHER,
  sky: (P, phase) => {
    const light = PHASE_LIGHT[phase] ?? PHASE_LIGHT.day!;
    return mix(P.far(P.pasture), light.haze, light.hazeT);
  },
  // D345: the shared badge's colours; the gold icon when a row has none of its own.
  readyBadge: { ...READY_BADGE, fallback: 'currency_gold' },
  // D422, D423: the heart on the yard, the store part of it; each plot's units counted.
  heart: true,
  dress: (p, { owned }) => {
    p.owned = owned;
  },
  top: strongholdTop,
  tall: true,
  flat: {
    scenery: paintCountry,
    ground: paintGround,
    fieldJob: () => [],
    machine: () => {},
    edges: paintEdges,
    plotEdges: paintPlotEdges,
    props: (p, showSigns) => arenaProps(p, showSigns),
    prop: paintArenaProp,
    standing: (buildings, parcels): Standing[] =>
      allUnits(buildings, parcels).map((u) => ({
        x: u.x,
        y: u.y,
        w: u.w,
        h: u.d,
        z: unitTop(u) * ZPX + 6,
      })),
    weather: paintWeather,
    // D420: smoke off the campfires and the forges' chimneys.
    smoke: (buildings, parcels = []) => {
      const out: Puff[] = [];
      for (const [x, y, z] of smokeTops(buildings, parcels, 6)) {
        const [cx, cy] = project(x, y, z * ZPX + 3);
        [0, 3.1, 6.2].forEach((delay, i) =>
          out.push({ cx: cx + i, cy, r: 3 + i * 1.5, delay: delay + (Math.round(x) % 3) }),
        );
      }
      return out;
    },
    ripples: (parcels) => {
      const out: Ripple[] = [];
      for (const pc of parcels) if (pc.t === 'pond') out.push(...pondRipples(pc.tiles));
      return out;
    },
  },
};
